import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import {
  CheckboxInput,
  Combobox,
  DateInput,
  EmailInput,
  PhoneInput,
  SelectInput,
  TextInput,
} from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import Toast from "@/components/Toast"
import {
  useAddPatientMutation,
  useEMRPatientQuery,
  useOrganizationsQuery,
} from "@/data/queries"
import { HttpError } from "@/data/requests"
import BloodGlucoseThresholds from "@/model/BloodGlucoseThresholds"
import BloodPressureThresholds from "@/model/BloodPressureThresholds"
import { Organization } from "@/model/Organization"
import { AddPatientDto, Patient } from "@/model/Patient"
import {
  BloodPressureCuffSize,
  ClinicalProtocolType,
  DeviceDistributionMethod,
} from "@/model/Prescription"
import {
  cuffSizes,
  deviceDistributionMethodOptions,
  patientOptInValues,
} from "@/pages/patient-details/model"
import { buildPath, ROUTES } from "@/routes"
import useCheckBloomlifeId from "@/utils/hooks/useCheckBloomlifeId"
import useCheckPatientMRN from "@/utils/hooks/useCheckPatientMRN"
import { useSearchParams } from "@/utils/hooks/useSearchParams"
import { usTimeZoneOptions } from "@/utils/timezoneOptions.ts"
import { generateBloomlifeId } from "@/utils/uuid-utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { Duration, LocalDate } from "@js-joda/core"
import { first, orderBy } from "lodash"
import { ChangeEvent, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { caregiverName, formatPhoneNumber } from "../patient-details/utils"
import { formSchema } from "./formSchema"
import { useSectionContext } from "@/components/sidebar/SectionContext"

type AddPatientPageParams = {
  mrn: string
  organizationId: string
  clinicalProtocolType: ClinicalProtocolType
}

type FormData = {
  medicalRecordNumber: string
  bloomlifeId: string
  firstName: string
  lastName: string
  emailAddress?: string
  phoneNumber: string
  dateOfBirth?: string
  preferredLocale?: string
  timeZone: string
  shippingAddress?: {
    address1?: string
    address2?: string
    city?: string
    state?: string
    zipCode?: string
  }
  insuranceProvider?: string
  clinicalProtocolIndex: number
  diagnosis?: string
  startDate: string
  caregiverId: string
  departmentId?: string
  estimatedDateOfDelivery?: string
  deliveryDate?: string
  cuffSize?: BloodPressureCuffSize
  patientOptedInClinically: "yes" | "no"
  deviceDistributionMethod?: DeviceDistributionMethod
  isAttCompatible?: boolean
}

const AddPatientPage = () => {
  const navigate = useNavigate()
  const { currentSectionRootPath, goBackToSectionRoot } = useSectionContext()
  const [searchParams] = useSearchParams<AddPatientPageParams>()
  const {
    mrn: medicalRecordNumber,
    organizationId,
    clinicalProtocolType,
  } = searchParams

  const [isErrorDialogVisible, setIsErrorDialogVisible] = useState(false)

  const { data: organizations, isLoading: isLoadingOrganizations } =
    useOrganizationsQuery()
  const { mutateAsync: addNewPatient, isPending: isAddingNewPatient } =
    useAddPatientMutation()

  const selectedOrganization = organizations?.find(
    (org: Organization) => org.id === organizationId
  )

  const {
    data: emrPatientData,
    isLoading: isCheckingEMR,
    error: emrPatientError,
  } = useEMRPatientQuery({
    organizationId,
    medicalRecordNumber: medicalRecordNumber.trim(),
    enabled: !!selectedOrganization && selectedOrganization.emrType !== "none",
  })

  const locales = selectedOrganization?.supportedLocales?.map(
    ({ locale, name }) => ({
      value: locale,
      title: name,
    })
  )

  const timeZoneOptions = usTimeZoneOptions

  const clinicalProtocols = (
    selectedOrganization?.clinicalProtocols ?? []
  ).filter((protocol) => protocol.type === clinicalProtocolType)

  const caregivers = selectedOrganization?.caregivers ?? []
  const departments = selectedOrganization?.departments ?? []

  const clinicalProtocolOptions = [
    { title: "-- Select a protocol --", value: -1, disabled: true },
    ...orderBy(
      clinicalProtocols.map((protocol, index) => ({
        title: protocol.name,
        value: index,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const caregiverOptions = [
    { title: "-- Select a provider --", value: "", disabled: true },
    ...orderBy(
      caregivers.map((caregiver) => ({
        title: caregiverName(caregiver),
        value: caregiver.id,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  const departmentOptions = [
    { title: "", value: "", disabled: true },
    ...orderBy(
      departments.map((department) => ({
        title: department.name,
        value: department.id,
      })),
      ({ title }) => title.toLowerCase()
    ),
  ]

  useEffect(() => {
    if (emrPatientError) {
      setIsErrorDialogVisible(true)
    }
  }, [emrPatientError])

  const {
    title: emrPatientErrorDialogTitle,
    message: emrPatientErrorDialogMessage,
  } = importPatientErrorInfo(emrPatientError)

  const initialFormValues: Partial<FormData> = Object.freeze({
    medicalRecordNumber: medicalRecordNumber,
    bloomlifeId: generateBloomlifeId(),
    firstName: emrPatientData?.firstName,
    lastName: emrPatientData?.lastName,
    phoneNumber: formatPhoneNumber(emrPatientData?.phoneNumber ?? ""),
    emailAddress: emrPatientData?.emailAddress,
    dateOfBirth: emrPatientData?.dateOfBirth?.toString(),
    preferredLocale: emrPatientData?.preferredLocale ?? first(locales)?.value,
    clinicalProtocolIndex: -1,
    timeZone: emrPatientData?.timeZone ?? selectedOrganization?.timeZone,
    startDate: LocalDate.now().toString(),
    cuffSize: clinicalProtocolType === "hypertension" ? "PENDING" : undefined,
    patientOptedInClinically: "no",
    shippingAddress: emrPatientData?.shippingAddress,
    isAttCompatible: undefined,
  })

  const { register, setValue, handleSubmit, formState, watch, trigger, reset } =
    useForm<FormData>({
      mode: "all",
      defaultValues: initialFormValues,
      resolver: zodResolver(
        formSchema({
          organization: selectedOrganization,
          protocol: clinicalProtocolType,
        })
      ),
    })
  const { errors } = formState
  const formValues = watch()

  useEffect(() => {
    if (selectedOrganization) {
      const newDefaults: Partial<FormData> = {
        ...initialFormValues,
        timeZone:
          emrPatientData?.timeZone || selectedOrganization.timeZone || "",
        preferredLocale:
          emrPatientData?.preferredLocale ||
          first(selectedOrganization?.supportedLocales)?.locale ||
          "",
      }
      reset({ ...formValues, ...newDefaults }, { keepDirtyValues: true })
    }
  }, [selectedOrganization, reset, emrPatientData])

  useEffect(() => {
    if (emrPatientData && selectedOrganization) {
      toast.custom((toastId) => (
        <Toast
          description="Patient information has been pre-filled from the EMR system."
          toastId={toastId}
        />
      ))
    }
  }, [emrPatientData, selectedOrganization])

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setValue("phoneNumber", formatted, { shouldValidate: true })
  }

  const {
    debouncedCheckMRN,
    patientWithMRN: existingPatientForMRN,
    isCheckingPatientMRN,
    isErrorCheckPatientMRN,
  } = useCheckPatientMRN({
    debounce: Duration.ofSeconds(1),
    organizationId: organizationId,
    medicalRecordNumber: medicalRecordNumber,
  })

  const {
    debouncedCheckBloomlifeId,
    isBloomlifeIdInUse,
    isCheckingBloomlifeId,
    isErrorCheckBloomlifeId,
    setIsBloomlifeIdInUse,
  } = useCheckBloomlifeId({
    debounce: Duration.ofSeconds(1),
  })

  const [confirmRedirect, setConfirmRedirect] = useState(false)

  const openConfirmRedirectModal = () => setConfirmRedirect(true)
  const closeConfirmRedirectModal = () => setConfirmRedirect(false)

  const onMRNChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const trimmedValue = value.trim()
    if (trimmedValue) {
      debouncedCheckMRN(trimmedValue)
    }
  }

  const onBloomlifeIdChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const trimmedValue = value.trim()
    setIsBloomlifeIdInUse(undefined)
    if (trimmedValue) {
      const isValid = await trigger("bloomlifeId")
      if (isValid) {
        debouncedCheckBloomlifeId(trimmedValue)
      }
    }
  }

  const generateNewBloomlifeId = () => {
    const newId = generateBloomlifeId()
    setValue("bloomlifeId", newId, { shouldValidate: true })
    setIsBloomlifeIdInUse(false)
  }

  const navigateToPatientDetailPage = (patient: Patient) => {
    navigate(
      buildPath(ROUTES.POPULATION.PATIENT_DETAIL, {
        bloomlifeId: patient.bloomlifeId,
      }),
      {
        replace: true,
      }
    )
  }

  const mrnCheckSucceeded = !isCheckingPatientMRN && !isErrorCheckPatientMRN

  // Check for false instead of undefined to ensure that the Bloomlife ID is not in use
  const bloomlifeIdCheckSucceeded = isBloomlifeIdInUse === false

  const canSubmit =
    !!selectedOrganization &&
    !isAddingNewPatient &&
    mrnCheckSucceeded &&
    !existingPatientForMRN &&
    bloomlifeIdCheckSucceeded

  const onSubmit = async (formData: FormData) => {
    try {
      const clinicalProtocol = clinicalProtocols[formData.clinicalProtocolIndex]
      const createNewPatientDto = toAddPatientDto({
        ...formData,
        clinicalProtocolName: clinicalProtocol.name,
        ...(clinicalProtocol.type === "diabetes"
          ? {
              clinicalProtocolType: "diabetes",
              thresholds: clinicalProtocol.bloodGlucoseThresholds,
            }
          : {
              clinicalProtocolType: "hypertension",
              thresholds: clinicalProtocol.bloodPressureThresholds,
            }),
      })
      const patient = await addNewPatient({
        organizationId,
        patient: createNewPatientDto,
      })
      navigateToPatientDetailPage(patient)
    } catch (error) {
      toast.custom(
        (toastId) => (
          <DevErrorToast
            userMessage="Something went wrong while adding the new patient"
            toastId={toastId}
            error={error}
          />
        ),
        { duration: Infinity }
      )
    }
  }

  const onSelectTimeZone = (value: string | null) => {
    setValue("timeZone", value ?? "", { shouldValidate: true })
  }

  const breadcrumbs = [
    { element: "Patients overview", href: currentSectionRootPath },
    { element: selectedOrganization?.longName ?? "-" },
    { element: medicalRecordNumber },
  ]

  return (
    <Page breadcrumbs={breadcrumbs}>
      <div className="mx-auto mb-6 w-full max-w-[1100px]">
        {isLoadingOrganizations || isCheckingEMR ? (
          <div className="flex h-[200px] items-center justify-center">
            <Spinner className="me-2" />
            <span>
              {isLoadingOrganizations
                ? "Loading organization data..."
                : "Checking EMR for patient data..."}
            </span>
          </div>
        ) : (
          <>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span
                  className="material-icons cursor-pointer rounded-md bg-primary/10 p-1 text-[28px]"
                  onClick={goBackToSectionRoot}
                >
                  keyboard_arrow_left
                </span>
                <div className="flex items-center justify-center gap-4">
                  <p className="text-4xl font-bold">Add patient</p>
                  {emrPatientData && (
                    <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800">
                      Imported from EMR
                    </span>
                  )}
                </div>
              </div>
            </div>
            <form
              className="mt-6"
              onSubmit={handleSubmit(onSubmit)}
              autoComplete="off"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="mrn" className="ml-1">
                    MRN*
                  </InputLabel>
                  <div className="relative w-full">
                    {isCheckingPatientMRN && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <Spinner />
                      </span>
                    )}
                    <TextInput
                      id="mrn"
                      placeholder="MRN"
                      disabled={!!emrPatientData}
                      isError={!!errors?.medicalRecordNumber?.message}
                      {...register("medicalRecordNumber", {
                        onChange: onMRNChange,
                      })}
                    />
                  </div>
                  <InputErrorMessage
                    message={
                      errors?.medicalRecordNumber?.message ? (
                        errors?.medicalRecordNumber?.message
                      ) : existingPatientForMRN ? (
                        <div className="flex items-center gap-1">
                          This MRN is already in use.
                          <button
                            type="button"
                            className="cursor-pointer text-primary/90 italic underline"
                            onClick={openConfirmRedirectModal}
                          >
                            View patient
                          </button>
                        </div>
                      ) : isErrorCheckPatientMRN ? (
                        "Something went wrong while checking the MRN"
                      ) : (
                        ""
                      )
                    }
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="bloomlife-id" className="ml-1">
                    Bloomlife ID*
                  </InputLabel>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      {isCheckingBloomlifeId && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <Spinner />
                        </span>
                      )}
                      {!isCheckingBloomlifeId &&
                        isBloomlifeIdInUse === false &&
                        !errors?.bloomlifeId?.message &&
                        formValues.bloomlifeId && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-green-500">
                            <span className="material-icons">check_circle</span>
                          </span>
                        )}
                      <TextInput
                        id="bloomlife-id"
                        placeholder="Enter Bloomlife ID"
                        onInput={(e) => {
                          e.currentTarget.value =
                            e.currentTarget.value.toUpperCase()
                        }}
                        isError={
                          !!errors?.bloomlifeId?.message || isBloomlifeIdInUse
                        }
                        {...register("bloomlifeId", {
                          onChange: onBloomlifeIdChange,
                        })}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="neutral"
                      onClick={generateNewBloomlifeId}
                      className="px-3 py-2"
                      title="Generate New ID"
                    >
                      <span className="material-icons text-lg">
                        published_with_changes
                      </span>
                    </Button>
                  </div>
                  <InputErrorMessage
                    message={
                      errors?.bloomlifeId?.message ||
                      (isBloomlifeIdInUse &&
                        "This Bloomlife ID is already in use.") ||
                      (isErrorCheckBloomlifeId &&
                        "Something went wrong while checking the Bloomlife ID")
                    }
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="first-name" className="ml-1">
                    First name*
                  </InputLabel>
                  <TextInput
                    id="first-name"
                    placeholder="First name"
                    isError={!!errors?.firstName?.message}
                    {...register("firstName")}
                  />
                  <InputErrorMessage
                    message={errors?.firstName?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="last-name" className="ml-1">
                    Last name*
                  </InputLabel>
                  <TextInput
                    id="last-name"
                    placeholder="Last name"
                    isError={!!errors?.lastName?.message}
                    {...register("lastName")}
                  />
                  <InputErrorMessage
                    message={errors?.lastName?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="date-of-birth" className="ml-1">
                    Date of birth
                  </InputLabel>
                  <DateInput
                    id="date-of-birth"
                    isError={!!errors?.dateOfBirth?.message}
                    max={LocalDate.now().toString()}
                    {...register("dateOfBirth")}
                  />
                  <InputErrorMessage
                    message={errors?.dateOfBirth?.message}
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="email" className="ml-1">
                    Email address
                  </InputLabel>
                  <EmailInput
                    id="email"
                    placeholder="Email address"
                    isError={!!errors?.emailAddress?.message}
                    {...register("emailAddress")}
                  />
                  <InputErrorMessage
                    message={errors?.emailAddress?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="phone-number" className="ml-1">
                    Phone number*
                  </InputLabel>
                  <PhoneInput
                    id="phone-number"
                    placeholder="Phone number"
                    isError={!!errors?.phoneNumber?.message}
                    {...register("phoneNumber")}
                    onChange={handlePhoneNumberChange}
                  />
                  <InputErrorMessage
                    message={errors?.phoneNumber?.message}
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="preferred-locale" className="ml-1">
                    Preferred language*
                  </InputLabel>
                  <SelectInput
                    id="preferred-locale"
                    options={locales || []}
                    {...register("preferredLocale")}
                  />
                  <InputErrorMessage
                    message={errors?.preferredLocale?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="insurance-provider" className="ml-1">
                    Insurance provider
                  </InputLabel>
                  <TextInput
                    id="insurance-provider"
                    placeholder="Insurance provider"
                    isError={!!errors?.insuranceProvider?.message}
                    {...register("insuranceProvider")}
                  />
                  <InputErrorMessage
                    message={errors?.insuranceProvider?.message}
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="my-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="time-zone" className="ml-1">
                    Time zone
                  </InputLabel>
                  <Combobox
                    id="time-zone"
                    options={timeZoneOptions}
                    value={formValues.timeZone}
                    onSelect={onSelectTimeZone}
                    placeholder="Search timezone..."
                    {...register("timeZone")}
                  />
                  <InputErrorMessage
                    message={errors?.timeZone?.message}
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="shipping-address" className="ml-1">
                    Shipping address
                  </InputLabel>
                  <TextInput
                    id="shipping-address"
                    placeholder="Shipping address"
                    isError={!!errors?.shippingAddress?.address1?.message}
                    {...register("shippingAddress.address1")}
                  />
                  <InputErrorMessage
                    message={errors?.shippingAddress?.address1?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="shipping-address-2" className="ml-1">
                    Address line 2
                  </InputLabel>
                  <TextInput
                    id="shipping-address-2"
                    placeholder="Address line 2"
                    isError={!!errors?.shippingAddress?.address2?.message}
                    {...register("shippingAddress.address2")}
                  />
                  <InputErrorMessage
                    message={errors?.shippingAddress?.address2?.message}
                    className="ml-1"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="shipping-city" className="ml-1">
                    City
                  </InputLabel>
                  <TextInput
                    id="shipping-city"
                    placeholder="City"
                    isError={!!errors?.shippingAddress?.city?.message}
                    {...register("shippingAddress.city")}
                  />
                  <InputErrorMessage
                    message={errors?.shippingAddress?.city?.message}
                    className="ml-1"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="shipping-state" className="ml-1">
                    State
                  </InputLabel>
                  <TextInput
                    id="shipping-state"
                    placeholder="State"
                    isError={!!errors?.shippingAddress?.state?.message}
                    {...register("shippingAddress.state")}
                  />
                  <InputErrorMessage
                    message={errors?.shippingAddress?.state?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="shipping-zip" className="ml-1">
                    Zip
                  </InputLabel>
                  <TextInput
                    id="shipping-zip"
                    placeholder="Zip"
                    isError={!!errors?.shippingAddress?.zipCode?.message}
                    {...register("shippingAddress.zipCode")}
                  />
                  <InputErrorMessage
                    message={errors?.shippingAddress?.zipCode?.message}
                    className="ml-1"
                  />
                </div>

                <div className="flex flex-row items-center gap-1">
                  <CheckboxInput
                    id="att-compatibility"
                    {...register("isAttCompatible")}
                  />
                  <InputLabel htmlFor="att-compatibility" className="ml-1">
                    AT&T Compatibility
                  </InputLabel>
                </div>
              </div>

              {/* Pregnancy Form */}
              <div className="mt-4 flex flex-col gap-4 rounded-lg bg-gray-200 px-8 pt-4 pb-6">
                <p className="ml-1 text-xl font-bold">Pregnancy</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="edd" className="ml-1">
                      Estimated delivery date
                    </InputLabel>
                    <DateInput
                      id="edd"
                      isError={!!errors?.estimatedDateOfDelivery?.message}
                      {...register("estimatedDateOfDelivery")}
                    />
                    <InputErrorMessage
                      message={errors?.estimatedDateOfDelivery?.message}
                      className="ml-1"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="delivery-date" className="ml-1">
                      Delivery date
                    </InputLabel>
                    <DateInput
                      id="delivery-date"
                      isError={!!errors?.deliveryDate?.message}
                      {...register("deliveryDate")}
                    />
                    <InputErrorMessage
                      message={errors?.deliveryDate?.message}
                      className="ml-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="patient-opted-in" className="ml-1">
                      Patient opted in clinically*
                    </InputLabel>
                    <SelectInput
                      id="patient-opted-in"
                      options={patientOptInValues}
                      {...register("patientOptedInClinically")}
                    />
                    <InputErrorMessage
                      message={errors?.patientOptedInClinically?.message}
                      className="ml-1"
                    />
                  </div>
                </div>
              </div>

              {/* Prescription Form */}
              <div className="mt-4 flex flex-col gap-4 rounded-lg bg-gray-200 px-8 pt-4 pb-6">
                <p className="ml-1 text-xl font-bold">
                  {clinicalProtocolType === "hypertension"
                    ? "Hypertension"
                    : "Diabetes"}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="clinical-protocol" className="ml-1">
                      Clinical protocol*
                    </InputLabel>
                    <SelectInput
                      id="clinical-protocol"
                      options={clinicalProtocolOptions}
                      defaultValue={-1}
                      {...register("clinicalProtocolIndex", {
                        valueAsNumber: true,
                      })}
                    />
                    <InputErrorMessage
                      message={errors?.clinicalProtocolIndex?.message}
                      className="ml-1"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="diabetes-start-date" className="ml-1">
                      Prescription start date*
                    </InputLabel>
                    <DateInput
                      id="diabetes-start-date"
                      isError={!!errors?.startDate?.message}
                      min={LocalDate.now().toString()}
                      {...register("startDate")}
                    />
                    <InputErrorMessage
                      message={errors?.startDate?.message}
                      className="ml-1"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <InputLabel htmlFor="diabetes-diagnosis" className="ml-1">
                    Diagnosis
                  </InputLabel>
                  <TextInput
                    id="diabetes-diagnosis"
                    placeholder="Diagnosis"
                    isError={!!errors?.diagnosis?.message}
                    {...register("diagnosis")}
                  />
                  <InputErrorMessage
                    message={errors?.diagnosis?.message}
                    className="ml-1"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="caregiver" className="ml-1">
                      Provider*
                    </InputLabel>
                    <SelectInput
                      id="caregiver"
                      options={caregiverOptions}
                      defaultValue=""
                      {...register("caregiverId")}
                    />
                    <InputErrorMessage
                      message={errors?.caregiverId?.message}
                      className="ml-1"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <InputLabel htmlFor="department" className="ml-1">
                      Department
                      {selectedOrganization?.emrType === "athena" ? "*" : ""}
                    </InputLabel>
                    <SelectInput
                      id="department"
                      options={departmentOptions}
                      defaultValue=""
                      {...register("departmentId")}
                    />
                    <InputErrorMessage
                      message={errors?.departmentId?.message}
                      className="ml-1"
                    />
                  </div>

                  {clinicalProtocolType === "hypertension" && (
                    <div className="flex flex-col gap-1">
                      <InputLabel htmlFor="cuff-size" className="ml-1">
                        Cuff size*
                      </InputLabel>
                      <SelectInput
                        id="cuff-size"
                        options={cuffSizes}
                        {...register("cuffSize")}
                      />
                      <InputErrorMessage
                        message={errors?.cuffSize?.message}
                        className="ml-1"
                      />
                    </div>
                  )}

                  {clinicalProtocolType === "diabetes" && (
                    <div className="flex flex-col gap-1">
                      <InputLabel
                        htmlFor="device-distribution-method"
                        className="ml-1"
                      >
                        Device distribution*
                      </InputLabel>
                      <SelectInput
                        id="device-distribution-method"
                        options={deviceDistributionMethodOptions}
                        {...register("deviceDistributionMethod")}
                      />
                      <InputErrorMessage
                        message={errors?.deviceDistributionMethod?.message}
                        className="ml-1"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="my-6 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="neutral"
                  onClick={goBackToSectionRoot}
                  disabled={isAddingNewPatient}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex items-center gap-3 px-6"
                  disabled={!canSubmit}
                >
                  {isAddingNewPatient && <Spinner className="size-4" />}
                  Add patient
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
      <Dialog
        title="Unsaved changes"
        isOpen={confirmRedirect}
        className="max-w-md"
        onClose={closeConfirmRedirectModal}
      >
        <p>
          Are you sure you want to navigate to that patient's page? Any unsaved
          changes on this page will be lost.
        </p>
        <div className="mt-8 flex justify-end gap-3">
          <Button
            variant="neutral"
            className="px-6"
            onClick={closeConfirmRedirectModal}
          >
            No
          </Button>
          <Button
            variant="primary"
            onClick={() => navigateToPatientDetailPage(existingPatientForMRN!)}
            className="flex items-center gap-3 px-6"
          >
            Yes
          </Button>
        </div>
      </Dialog>
      <Dialog
        title={
          <div className="flex items-center gap-2">
            <span className="material-icons-outlined -ms-px text-red-500">
              error
            </span>
            {emrPatientErrorDialogTitle}
          </div>
        }
        isOpen={isErrorDialogVisible}
        className="max-w-md"
        onClose={() => setIsErrorDialogVisible(false)}
      >
        {emrPatientErrorDialogMessage}
      </Dialog>
    </Page>
  )
}

const importPatientErrorInfo = (error: Error | null) => {
  const defaultError = {
    title: "Error checking MRN",
    message:
      "Something went wrong while checking the MRN. Make sure the MRN and the organization are correct before continuing, and if so, take extra caution to fill in the information correctly from the order.",
    error: error,
  }
  if (!(error instanceof HttpError) || !error.responseJson) {
    return defaultError
  }
  const errorCode = error.responseJson.code
  switch (errorCode) {
    case "PATIENT_NOT_FOUND_IN_EMR":
      return {
        title: "Patient not found in EMR",
        message:
          "Make sure the MRN and the organization are correct before continuing, and if so, take extra caution to fill in the information correctly from the order.",
        error: error,
      }
    case "EMR_PATIENT_LOOKUP_FAILED":
      return {
        title: "EMR patient lookup failed",
        message:
          "Patient information cannot be prefilled due to an issue with the EMR connection. Take extra caution to fill in the information correctly from the order, or stop and try again later.",
        error: error,
      }
    default:
      return defaultError
  }
}

const toAddPatientDto = (
  formData: FormData & { clinicalProtocolName: string } & (
      | { clinicalProtocolType: "diabetes"; thresholds: BloodGlucoseThresholds }
      | {
          clinicalProtocolType: "hypertension"
          thresholds: BloodPressureThresholds
        }
    )
): AddPatientDto => {
  const protocolType = formData.clinicalProtocolType
  const prescription = {
    clinicalProtocolName: formData.clinicalProtocolName,
    diagnosis: formData.diagnosis || undefined,
    startDate: formData.startDate,
    caregiverId: formData.caregiverId || undefined,
    caregiverDepartmentId: formData.departmentId || undefined,
    deviceDistributionMethod:
      formData.deviceDistributionMethod ?? "home-delivery-or-reuse",
  }

  return {
    medicalRecordNumber: formData.medicalRecordNumber,
    bloomlifeId: formData.bloomlifeId,
    firstName: formData.firstName,
    lastName: formData.lastName,
    phoneNumber: `+1${formData.phoneNumber.replace(/\D/g, "")}`, // Send +1 version to backend
    emailAddress: formData.emailAddress || undefined,
    dateOfBirth: formData.dateOfBirth || undefined,
    preferredLocale: formData.preferredLocale || undefined,
    timeZone: formData.timeZone || undefined,
    insuranceProvider: formData.insuranceProvider || undefined,
    shippingAddress: formData.shippingAddress
      ? {
          address1: formData.shippingAddress.address1 || undefined,
          address2: formData.shippingAddress.address2 || undefined,
          city: formData.shippingAddress.city || undefined,
          state: formData.shippingAddress.state || undefined,
          zipCode: formData.shippingAddress.zipCode || undefined,
        }
      : undefined,
    pregnancy: {
      estimatedDateOfDelivery: formData.estimatedDateOfDelivery || null,
      deliveryDate: formData.deliveryDate || null,
      patientOptedInClinically: formData.patientOptedInClinically === "yes",
    },
    diabetesPrescription:
      protocolType === "diabetes"
        ? {
            ...prescription,
            type: "diabetes",
            bloodGlucoseThresholds: formData.thresholds,
          }
        : undefined,
    hypertensionPrescription:
      protocolType === "hypertension"
        ? {
            ...prescription,
            type: "hypertension",
            bloodPressureThresholds: formData.thresholds,
            cuffSize: formData.cuffSize,
          }
        : undefined,
    isAttCompatible: formData.isAttCompatible,
  }
}

export default AddPatientPage
